import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { GuidedProcessSupportBlock } from '../../global/components/guided-process-support-block';
import { GpsType } from '../../global/components/layout/layout-models';
import { MainContent } from '../../global/components/layout/main-content-container';
import { PageContainer } from '../../global/components/layout/page-container';
import { PageHeader } from '../../global/components/layout/page-header';
import { SideBarContent } from '../../global/components/layout/sidebar-content';
import { WysiwygWithReactLink } from '../../global/components/wysiwyg-with-react-link';
import { MapNotification } from '../../global/helpers/form-data-helper';
import { useGpsType } from '../../global/hooks/useGpsType';
import { TcsmSiteSettingsProps } from '../../global/models/epi-model';
import { TcsmStates } from '../../root/tcsm-model';
import { ListPagination } from '../../search/components/list-pagination';
import { SearchApp } from '../../search/components/search-app';
import { SearchList } from '../../search/components/search-list';
import { SearchProps } from '../arc/models/arc-epi-props';
import { NotificationItem } from './notification-item';
import { dismissNotification } from './notification-helpers';
import { Notification, NotificationStatus } from './notification-models';


export function NotificationAlertPage() {

	const [delayPassed, setDelayPassed] = useState(false);
	const [activeTab, setActiveTab] = useState<'unread' | 'all'>(() =>
		new URLSearchParams(window.location.search).get('tab') === 'unread' ? 'unread' : 'all',
	);
	const [listAnimKey, setListAnimKey] = useState(0);
	const allNotifications = useSelector<TcsmStates, Notification[]>(state => state?.notificationState?.notifications);
	const notificationLoaded = useSelector<TcsmStates, boolean>(state => state?.notificationState?.notificationLoaded);
	const searchSettings = useSelector<TcsmStates, SearchProps>(state => state?.epiProps?.searchSettingsState?.epiContent);
	const siteSettings = useSelector<TcsmStates, TcsmSiteSettingsProps>(state => state?.epiProps?.siteSettingsState?.siteSettings);
	const siteSettingsLoaded = useSelector<TcsmStates, boolean>(state => state?.epiProps?.siteSettingsState?.siteSettingsLoaded);
	const gpsItem = useGpsType(GpsType.ALERT_NOTIFICATION);
	const dispatch = useDispatch();

	const unreadNotifications = allNotifications?.filter(n => n.notification_status === NotificationStatus.INITIAL) || [];
	const filteredList = activeTab === 'unread' ? unreadNotifications : allNotifications;

	const markAllAsRead = () => {
		const dismissable = (allNotifications || [])
			.map(n => MapNotification(n, siteSettings?.notificationMapping))
			.filter(n => !n.is_mandatory && n.notification_status === NotificationStatus.INITIAL);
		if (dismissable.length === 0) return;
		dismissable.forEach(n => dismissNotification(dispatch, n));
		setListAnimKey(k => k + 1);
	};

	const selectTab = (tab: 'unread' | 'all') => {
		if (tab === activeTab) return;
		setActiveTab(tab);
		setListAnimKey(k => k + 1);
	};

	useEffect(() => {
		const timer = setTimeout(() => setDelayPassed(true), 1000);
		return () => clearTimeout(timer);
	}, []);

	function RenderNoNotificationsMessage(){
		if (filteredList?.length > 0) return null;
		if (activeTab === 'unread') return (
			<div className="notification-page-caught-up">
				<p className="notification-page-caught-up-text">You&apos;re all caught up</p>
				<button type="button" className="notification-page-caught-up-link" onClick={() => selectTab('all')}>
					View read notifications
				</button>
			</div>
		);
		return (<WysiwygWithReactLink content={siteSettings?.alertNotificationNoNotificationMessage} dispatch={dispatch} />);
	}

	if (!siteSettingsLoaded || !notificationLoaded || !delayPassed) {
		return <LoadingCircle />;
	}

	return (

		<PageContainer>
			<MainContent>
					<PageHeader headerText={siteSettings?.alertNotificationPageTitle || 'View All Notifications'} />

					<div className="notification-page-toolbar">
						<div className="notification-page-tabs">
							<button
								type="button"
								className={`notification-page-tab ${activeTab === 'unread' ? 'notification-page-tab--active' : ''}`}
								onClick={() => selectTab('unread')}
							>
								Unread ({unreadNotifications.length})
							</button>
							<button
								type="button"
								className={`notification-page-tab ${activeTab === 'all' ? 'notification-page-tab--active' : ''}`}
								onClick={() => selectTab('all')}
							>
								All ({allNotifications?.length || 0})
							</button>
						</div>
						{unreadNotifications.length > 0 && (
							<button type="button" className="notification-page-mark-all" onClick={markAllAsRead}>
								Mark all as read
							</button>
						)}
					</div>

					<RenderNoNotificationsMessage />

					<div key={listAnimKey} className="notification-page-list-fade">
					<SearchApp initialList={filteredList} appName="notification-page" resetPaginationKey={activeTab}>
						<SearchList
							renderList={
								(notification: Notification) => {
									const mappedNotification = MapNotification(notification, siteSettings?.notificationMapping);
									return <NotificationItem key={notification.notification_id.toString()} notification={mappedNotification} onDismiss={n => dismissNotification(dispatch, n)} mandatoryTooltip={siteSettings?.mandatoryNotificationTooltip} animateRemoval={activeTab === 'unread'} />;
								}
							}
							renderPagination={
								(totalCounts, name) => {
									return <ListPagination name={name} totalCounts={totalCounts} totalPages={Math.ceil(totalCounts / 10)} nextButton={searchSettings?.nextButton} prevButton={searchSettings?.previousButton} scrollToTopOnPageChange />;
								}
							}
						/>
					</SearchApp>
					</div>
			</MainContent>
			<SideBarContent>
				<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
			</SideBarContent>
		</PageContainer>


	);

}
